import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

import { getSelectedBranchId } from '../utils/branch';

var config = process.env;

// Panel de clientes (ENI-248).
//
// La salida de este panel tiene que ser ACCIONABLE: el dueño no quiere saber "cuántos clientes
// en riesgo hay", quiere la lista para llamarlos. Por eso el segmento se elige y se exporta.

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
const number = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE'));
const percent = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// El orden es el del ciclo de vida, no alfabético: se lee como un embudo.
// "Todos" no es un segmento: es el ranking completo del período, que es la pregunta más
// frecuente ("quién me deja más plata") y la que no se podía hacer. Va primero por eso.
// La lista llega entera del backend (hasta 300), así que el paginado es de cliente: no hay
// fetch, no hay recarga y no hay salto de scroll. 25 por página es lo mismo que usa el stock
// parado.
const CLIENTES_POR_PAGINA = 25;

const RANKING = { key: 'ranking', label: 'Ranking del período', ayuda: 'Todos los clientes que compraron en el rango elegido, de mayor a menor gasto.', color: 'primary' };

const SEGMENTOS = [
    { key: 'campeon', label: 'Campeones', ayuda: 'Compraron 3+ veces en los últimos 90 días.', color: 'success' },
    { key: 'activo', label: 'Activos', ayuda: 'Compraron en los últimos 90 días.', color: 'info' },
    { key: 'en_riesgo', label: 'En riesgo', ayuda: 'Compraban seguido y hace más de 90 días que no vuelven.', color: 'warning' },
    { key: 'tibio', label: 'Tibios', ayuda: 'Compraron alguna vez en el último año.', color: 'secondary' },
    { key: 'dormido', label: 'Dormidos', ayuda: 'Hace más de un año que no compran.', color: 'dark' },
];

function toIsoDate(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

class CustomerAnalyticsComponent extends Component {
    constructor(props) {
        super(props);
        const hoy = new Date();
        const desde = new Date();
        desde.setMonth(desde.getMonth() - 11);
        this.state = { from: desde, to: hoy, segmento: 'ranking', paginaClientes: 1, loaded: false, error: null, data: null };
    }

    componentDidMount() { this.cargar(); }

    cargar = () => {
        this.setState({ loaded: false, error: null });
        const params = new URLSearchParams({ from: toIsoDate(this.state.from), to: toIsoDate(this.state.to) });
        const branchId = getSelectedBranchId();
        if (branchId) params.set('branchId', branchId);

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}analytics/customers?${params.toString()}`, {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        })
            .then((r) => r.json())
            .then((body) => {
                if (body.error > 0) {
                    if (body.error === 1002) { window.location.href = localStorage.getItem('homePageURL'); return; }
                    this.setState({ loaded: true, error: body.message, data: null });
                    return;
                }
                this.setState({ loaded: true, data: body.data, error: null, paginaClientes: 1 });
            })
            .catch((e) => this.setState({ loaded: true, error: e.message, data: null }));
    };

    clientesDelSegmento() {
        const { data, segmento } = this.state;
        if (!data) return [];
        // El ranking sale ya ordenado por lo gastado EN EL RANGO; los segmentos vienen con su
        // propio tope. Antes esto filtraba un top-100 GLOBAL, así que la tarjeta decía "En
        // riesgo: 117" y la tabla mostraba 33 — los únicos 33 de esos 117 que entraban en el
        // top global. La lista se veía completa y no lo estaba.
        if (segmento === 'ranking') return data.rfm.ranking || [];
        return (data.rfm.bySegment && data.rfm.bySegment[segmento]) || [];
    }

    // Cuántos hay en total contra cuántos se muestran, para que el corte no se disfrace de lista
    // completa.
    totalDelSegmento() {
        const { data, segmento } = this.state;
        if (!data) return 0;
        if (segmento === 'ranking') return data.rfm.rankingTotal || 0;
        const seg = data.rfm.segments[segmento];
        return seg ? seg.customers : 0;
    }

    exportarSegmento = () => {
        const clientes = this.clientesDelSegmento();
        if (clientes.length === 0) return;
        const filas = [['Cédula', 'Nombre', 'Compras período', 'Gastado período', 'Compras histórico', 'Gastado histórico', 'Ticket promedio', 'Última compra', 'Días sin comprar']];
        clientes.forEach((c) => {
            filas.push([c.cedula, `"${(c.name || '').replace(/"/g, '""')}"`,
                c.rangeFrequency, Math.round(c.rangeMonetary),
                c.frequency, Math.round(c.monetary),
                c.avgTicket == null ? '' : Math.round(c.avgTicket),
                c.lastPurchase, c.recencyDays]);
        });
        const csv = '﻿' + filas.map((f) => f.join(';')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        // El nombre dice el corte cuando lo hay: el backend topea en 300 y "Tibios" son 2.063.
        // Un archivo llamado `clientes-tibio.csv` con 300 filas se usa para una campaña creyendo
        // que están todos, y nadie se entera de los 1.763 que faltan.
        const recorte = this.totalDelSegmento() > clientes.length ? `-top${clientes.length}` : '';
        a.download = `clientes-${this.state.segmento}${recorte}-${toIsoDate(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    render() {
        const { loaded, error, data } = this.state;

        if (!loaded) return <div className="text-center text-muted py-5">Cargando…</div>;
        if (error) return <div className="alert alert-warning">{error}</div>;
        if (!data) return null;

        const seleccionados = this.clientesDelSegmento();
        // `seleccionados` sigue siendo la lista COMPLETA: la usan el contador del encabezado y el
        // export. Sólo se pagina lo que se dibuja.
        const paginas = Math.max(1, Math.ceil(seleccionados.length / CLIENTES_POR_PAGINA));
        const pagina = Math.min(this.state.paginaClientes, paginas);
        const desdeIndice = (pagina - 1) * CLIENTES_POR_PAGINA;
        const enPantalla = seleccionados.slice(desdeIndice, desdeIndice + CLIENTES_POR_PAGINA);

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap align-items-end">
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Cohortes desde</label>
                                <DatePicker selected={this.state.from} onChange={(d) => this.setState({ from: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mr-3 mb-2">
                                <label className="d-block" style={{ fontSize: 12 }}>Hasta</label>
                                <DatePicker selected={this.state.to} onChange={(d) => this.setState({ to: d })} dateFormat="dd/MM/yyyy" className="form-control" />
                            </div>
                            <div className="mb-2">
                                <button className="btn btn-primary" onClick={this.cargar}>Aplicar</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Ranking + segmentos ── */}
                <div className="row">
                    <div className="col-lg col-md-4 col-sm-6 mb-3">
                        <div
                            className={`card card-small h-100 ${this.state.segmento === 'ranking' ? 'border-primary' : ''}`}
                            style={{ cursor: 'pointer', borderWidth: this.state.segmento === 'ranking' ? 2 : undefined }}
                            onClick={() => this.setState({ segmento: 'ranking', paginaClientes: 1 })}
                        >
                            <div className="card-body p-3">
                                <span className="badge badge-primary mb-2">{RANKING.label}</span>
                                <h6 className="my-1">{number(data.rfm.rankingTotal)}</h6>
                                <span className="d-block text-muted" style={{ fontSize: 11 }}>
                                    {money(data.rfm.rankingMonetary)}
                                </span>
                                <span className="d-block text-muted mt-1" style={{ fontSize: 11 }}>{RANKING.ayuda}</span>
                            </div>
                        </div>
                    </div>
                    {SEGMENTOS.map((s) => {
                        const seg = data.rfm.segments[s.key] || { customers: 0, monetary: 0 };
                        const activo = this.state.segmento === s.key;
                        return (
                            <div className="col-lg col-md-4 col-sm-6 mb-3" key={s.key}>
                                {/* La tarjeta es un filtro: elegir un segmento cambia la lista de abajo. */}
                                <div
                                    className={`card card-small h-100 ${activo ? 'border-primary' : ''}`}
                                    style={{ cursor: 'pointer', borderWidth: activo ? 2 : undefined }}
                                    onClick={() => this.setState({ segmento: s.key, paginaClientes: 1 })}
                                >
                                    <div className="card-body p-3">
                                        <span className={`badge badge-${s.color} mb-2`}>{s.label}</span>
                                        <h6 className="my-1">{number(seg.customers)}</h6>
                                        <span className="d-block text-muted" style={{ fontSize: 11 }}>{money(seg.monetary)}</span>
                                        <span className="d-block text-muted mt-1" style={{ fontSize: 11 }}>{s.ayuda}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── La lista accionable ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="m-0">{(this.state.segmento === 'ranking' ? RANKING : SEGMENTOS.find((s) => s.key === this.state.segmento)).label}</h6>
                            <span className="text-muted" style={{ fontSize: 12 }}>
                                {(this.state.segmento === 'ranking' ? RANKING : SEGMENTOS.find((s) => s.key === this.state.segmento)).ayuda}
                                {' '}
                                {this.totalDelSegmento() > seleccionados.length
                                    ? <>Se muestran los {number(seleccionados.length)} de mayor gasto, de {number(this.totalDelSegmento())}.</>
                                    : <>Son {number(seleccionados.length)}, todos listados.</>}
                            </span>
                        </div>
                        {/* El rótulo dice cuántos baja de verdad. "Exportar este segmento" sobre
                            2.063 tibios bajaba 300 sin decirlo. */}
                        <button className="btn btn-white btn-sm" onClick={this.exportarSegmento} disabled={seleccionados.length === 0}>
                            {this.totalDelSegmento() > seleccionados.length
                                ? `Exportar los ${number(seleccionados.length)} listados`
                                : 'Exportar este segmento'}
                        </button>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0" style={{ width: 40 }}>#</th>
                                    <th className="border-0">Cliente</th>
                                    <th className="border-0 text-right">Compras del período</th>
                                    <th className="border-0 text-right">Gastado en el período</th>
                                    <th className="border-0 text-right">Gastado histórico</th>
                                    <th className="border-0 text-right">Ticket promedio</th>
                                    <th className="border-0 text-right">Última compra</th>
                                    <th className="border-0 text-right">Días sin comprar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {seleccionados.length === 0 && (
                                    <tr><td colSpan="8" className="text-center text-muted py-3">Ningún cliente para este filtro.</td></tr>
                                )}
                                {enPantalla.map((c, i) => (
                                    <tr key={c.cedula}>
                                        {/* El número es el puesto ABSOLUTO, no el de la página: es un
                                            ranking, y reiniciarlo en cada página pondría cinco "1". */}
                                        <td className="text-muted">{desdeIndice + i + 1}</td>
                                        <td>{c.name || <span className="text-muted">{c.cedula}</span>}</td>
                                        <td className="text-right">{number(c.rangeFrequency)}</td>
                                        {/* Las dos columnas juntas: el filtro de arriba manda sobre la
                                            del período, y el histórico da el contexto de quién es. */}
                                        <td className="text-right font-weight-bold">{money(c.rangeMonetary)}</td>
                                        <td className="text-right text-muted">{money(c.monetary)}</td>
                                        <td className="text-right">{money(c.avgTicket)}</td>
                                        <td className="text-right">{c.lastPurchase}</td>
                                        <td className="text-right">{number(c.recencyDays)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Paginado de cliente: 300 filas hacían eterno llegar a los bloques de
                            abajo, que quedaban enterrados detrás de la lista. */}
                        {paginas > 1 && (
                            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Página {number(pagina)} de {number(paginas)}
                                    {' · '}mostrando {number(desdeIndice + 1)}–{number(desdeIndice + enPantalla.length)}
                                    {' de '}{number(seleccionados.length)}
                                </span>
                                <div className="btn-group btn-group-sm">
                                    <button
                                        className="btn btn-white"
                                        disabled={pagina <= 1}
                                        onClick={() => this.setState({ paginaClientes: pagina - 1 })}
                                    >Anterior</button>
                                    <button
                                        className="btn btn-white"
                                        disabled={pagina >= paginas}
                                        onClick={() => this.setState({ paginaClientes: pagina + 1 })}
                                    >Siguiente</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Nuevos vs recurrentes ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Nuevos vs. recurrentes</h6>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            "Nuevo" es quien hizo su PRIMERA compra ese mes, mirando todo el histórico.
                        </span>
                    </div>
                    <div className="card-body">
                        <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={data.cohorts}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {/* isAnimationActive={false}: si el componente re-renderiza mientras corre el
                                    tween de entrada, recharts deja las series congeladas — acá quedaban en
                                    altura CERO, con ejes y leyenda dibujados, así que el gráfico parecía
                                    "sin datos" en vez de roto. */}
                                <Bar dataKey="new" name="Nuevos" stackId="a" fill="#17c671" isAnimationActive={false} />
                                <Bar dataKey="returning" name="Recurrentes" stackId="a" fill="#c4d8f0" isAnimationActive={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ── LTV por cohorte ── */}
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Cuánto deja cada camada</h6>
                        {/* La advertencia va en la pantalla y no en el código: una cohorte reciente
                            SIEMPRE parece peor porque tuvo menos tiempo de acumular. */}
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Gasto total promedio de los clientes según el mes en que compraron por primera vez.
                            Las camadas recientes tuvieron menos tiempo de acumular: se leen como curva, no como ranking.
                        </span>
                    </div>
                    <div className="card-body">
                        <ResponsiveContainer width="100%" height={240}>
                            <ComposedChart data={data.ltv}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="cohort" />
                                <YAxis yAxisId="izq" />
                                <YAxis yAxisId="der" orientation="right" />
                                <Tooltip />
                                <Legend />
                                <Bar yAxisId="der" dataKey="customers" name="Clientes" fill="#e1e5eb" isAnimationActive={false} />
                                <Line yAxisId="izq" dataKey="ltv" name="Gasto promedio" stroke="#007bff" strokeWidth={2} dot={false} isAnimationActive={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* ── Carritos, crédito y usados ── */}
                <div className="row">
                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Carritos abandonados</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>Valorizados al precio de hoy.</span>
                            </div>
                            <div className="card-body">
                                <p className="mb-2"><strong>{money(data.carts.totalValue)}</strong> en {number(data.carts.items.length)} carrito(s)</p>
                                {data.carts.items.length === 0
                                    ? <p className="text-muted mb-0" style={{ fontSize: 13 }}>Sin carritos abiertos.</p>
                                    : (
                                        <ul className="mb-0 pl-3" style={{ fontSize: 13 }}>
                                            {data.carts.items.slice(0, 5).map((c) => (
                                                <li key={c.accountId}>{c.name || c.email}: {money(c.value)}</li>
                                            ))}
                                        </ul>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <h6 className="m-0">Crédito en tienda</h6>
                                <span className="text-muted" style={{ fontSize: 12 }}>El saldo vivo es un pasivo.</span>
                            </div>
                            <div className="card-body">
                                <p className="mb-1">Emitido: {money(data.credit.issued)}</p>
                                <p className="mb-1">Usado: {money(data.credit.redeemed)}</p>
                                <p className="mb-0"><strong>Se debe: {money(data.credit.outstanding)}</strong>
                                    {' '}<span className="text-muted" style={{ fontSize: 12 }}>({number(data.credit.customers)} cliente/s)</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 col-md-12 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom"><h6 className="m-0">Juegos usados</h6></div>
                            <div className="card-body">
                                <p className="mb-1">{number(data.used.total)} ingresado(s), {number(data.used.sold)} vendido(s)</p>
                                <p className="mb-1">Crédito pactado: {money(data.used.creditAgreed)}</p>
                                <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                                    {data.used.avgDaysToSell == null
                                        ? 'Ninguno vendido todavía: no hay demora que promediar.'
                                        : `Tardan ${data.used.avgDaysToSell} día(s) en venderse, en promedio.`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default CustomerAnalyticsComponent;
